import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Eye, TrendingUp } from "lucide-react";
import { assetUrl } from "@/utils/assetUrl";
import client from "@/Graphql/apolloClient";
import { Get_ThreeBillboard } from "@/Graphql/Queries";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/utils/authContext";
import { generateMockData } from "@/Graphql/MockData";


const fallbackMillionViewInventories = [
  {
    id: "pop1",
    title: "Downtown Digital Display",
    location: "New York",
    price: 5500,
    category: "Premium",
    viewsPerWeek: "1.2M",
    imageUrl: `${assetUrl("digital-billboards-01_1753792412449.jpg")}`,
  },
  {
    id: "pop2",
    title: "Highway Premium Billboard",
    location: "California",
    price: 4200,
    category: "Premium",
    viewsPerWeek: "1.1M",
    imageUrl: `${assetUrl("landing_bb.png")}`,
  },
  {
    id: "pop3",
    title: "Mall Digital Screen",
    location: "Florida",
    price: 3800,
    category: "Premium",
    viewsPerWeek: "1.0M",
    imageUrl: `${assetUrl("landing_bb.png")}`,
  }
];

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Premium':
      return 'bg-primary-600 text-white';
    case 'High Traffic':
      return 'bg-forest-custom text-white';
    default:
      return 'bg-gray-500 text-white';
  }
};

interface MillionViewInventoriesProps {
  base_path: string;
  isExploreEnabled: boolean;
  isLoginEnabled: boolean;
  countryId: string;
  exposure: string;
}

export default function MillionViewInventories({ base_path, isExploreEnabled, isLoginEnabled, countryId, exposure }: MillionViewInventoriesProps) {

  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [millionViewInventories, setMillionViewInventories] = useState<any[]>([]);
  const [isHovered, setIsHovered] = useState(false);
  const companyId = import.meta.env.VITE_APP_COMPANY_ID;
  const variables = useMemo(() => ({
    companyId: "", // Use the actual companyId value
    size: 10,
    countryId: "", // Correct variable name for the query
    dsp: exposure     // <-- Added dsp (replace "defaultDSP" as needed)
  }), [companyId, countryId]);

  const [loading, setLoading] = useState(false);
  const { isLoggedIn } = useAuth();

  const navigate = useNavigate();

  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState<string>("");
  const [sectionSecondaryHeader, setSectionSecondaryHeader] = useState<string>("");
  const [sectionDescription, setSectionDescription] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`
        );
        const data = await response.json();

        const landingPageData = data.find(
          (page: any) => page.name === "Landing Page"
        );
        const testimonialsData = landingPageData?.fields?.find((field: any) =>
          field?.key?.includes("highImpactLocationsPanel")
        );

        // Fetch section title and description
        const sectionTitleField = testimonialsData?.components?.find((comp: any) => comp.key === "highImpact_title");
        const sectionPrimaryHeaderField = testimonialsData?.components?.find((comp: any) => comp.key === "highImpact_primary_header");
        const sectionSecondaryHeaderField = testimonialsData?.components?.find((comp: any) => comp.key === "highImpact_secondary_header");
        const sectionDescriptionField = testimonialsData?.components?.find((comp: any) => comp.key === "highImpact_description");

        setSectionTitle(sectionTitleField?.defaultValue || "");
        setSectionPrimaryHeader(sectionPrimaryHeaderField?.defaultValue || "");
        setSectionSecondaryHeader(sectionSecondaryHeaderField?.defaultValue || "");
        setSectionDescription(sectionDescriptionField?.defaultValue || "");

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchMillionViewInventories = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await client.query({
        query: Get_ThreeBillboard,
        variables: variables
      });
      if (data.topThreeBillboard && data.topThreeBillboard.length > 0) {
        setMillionViewInventories(data.topThreeBillboard.map((item: any) => ({
          id: item.billboardObjectId,
          title: item.displayName,
          location: item.stateName,
          price: typeof item.perDayPrice === 'number' ? item.perDayPrice : (typeof item.perDayPrice === 'string' ? parseFloat(item.perDayPrice.replace(/,/g, '')) : null),
          currencyCode: item.currencyCode || 'N/A',
          category: item.billboardType || 'Premium',
          viewsPerWeek: item?.totalViews ? item?.totalViews : 'N/A',
          imageUrl: item.thumbnailPath && item.thumbnailPath.trim() !== ''
            ? item.thumbnailPath
            : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
        })));
      } else {
        setMillionViewInventories([]);
      }
    } catch (error) {
      setMillionViewInventories([]);
    } finally {
      setLoading(false);
    }
  }, [variables]);

  useEffect(() => {
    const fetchMockData = async () => {
      try {
        const mockResponse = await generateMockData(Get_ThreeBillboard);
        let mockData = mockResponse.data?.topThreeBillboard || [];

        if (mockData && mockData.length > 0) {
          mockData = mockData.filter((item: any) => (item.billboardType || '').toLowerCase() !== 'classic-transit');
          const filteredData = mockData.filter((item: any) => {
            const type = (item.billboardType || item.type || '').toLowerCase().trim();
            return type === 'classic' || type === 'digital';
          });
          setMillionViewInventories(filteredData.map((item: any) => ({
            id: item.billboardObjectId,
            title: item.displayName,
            location: item.stateName,
            price:
              typeof item.perDayPrice === 'number'
                ? item.perDayPrice
                : typeof item.perDayPrice === 'string'
                  ? parseFloat(item.perDayPrice.replace(/,/g, ''))
                  : null,
            currencyCode: item.currencyCode || 'N/A',
            category: item.billboardType || 'Premium',
            viewsPerWeek: item?.totalViews ? item?.totalViews : 'N/A',
            imageUrl:
              item.thumbnailPath && item.thumbnailPath.trim() !== ''
              ? item.thumbnailPath
              : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
          })));

        } else {
          throw new Error('Mock data was empty or invalid');
        }
      } catch (error) {
        console.error("Error fetching mock explore properties:", error);
        setMillionViewInventories([]); // Use fallback data when no IDs and not mock mode
        setLoading(false);

      } finally {
        setLoading(false);
      }
    };

    if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
      setLoading(true);
      fetchMockData(); // Always fetch mock data when enabled
    } else {
      fetchMillionViewInventories(); // Fetch real data if selected IDs are present
    }
  }, [companyId, countryId, fetchMillionViewInventories]);

  // Update items per view based on screen size
  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  // Auto-scroll functionality
  useEffect(() => {
    if (isHovered) return; 
    
    const maxIndex = Math.max(millionViewInventories.length - itemsPerView, 0);
    const interval = setInterval(() => {
      setCurrentIndex(prevIndex =>
        prevIndex < maxIndex ? prevIndex + 1 : 0
      );
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [itemsPerView, millionViewInventories.length, isHovered]);

  const handleViewDetails = (inventoryItem: any) => {
    if (!isLoggedIn) {
      // Get the billboard type from the inventory item
      const billboardType = inventoryItem.billboardType;
      navigate(`${base_path}/billboard/${inventoryItem.billboardObjectId}?type=${encodeURIComponent(billboardType)}`, {
        state: {
          currentPrice: inventoryItem.price,
          priceFormat: 'landing-page-million'
        }
      });
    }
    else {
      // Get the billboard type from the inventory item
      const billboardType = inventoryItem.billboardType;
      navigate(`${base_path}/property-details/${inventoryItem.billboardObjectId}?type=${encodeURIComponent(billboardType)}`, {
        state: {
          currentPrice: inventoryItem.price,
          startDate: new Date().toISOString(),
          endDate: (() => {
            const now = new Date();
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            return lastDay.toISOString();
          })(),
          priceFormat: 'landing-page-million'
        }
      });
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-[#e6f9f3] to-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-3 sm:px-4 py-2 rounded-full text-sm shadow-sm mb-4">
            <TrendingUp className="h-4 w-4 text-primary-600" />
            <span className="text-lg sm:text-xl leading-relaxed">{sectionTitle}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-3 sm:mb-4">
            <span className="text-gray-900 block text-primary-600">{sectionPrimaryHeader}</span>
            <span className="bg-gradient-to-r from-primary-600 to-forest-custom text-green-600 bg-clip-text block">{sectionSecondaryHeader}</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {sectionDescription}
          </p>
        </div>

        {/* Auto-scrolling inventory cards */}
        <div 
          className="relative overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {millionViewInventories.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No million views found</p>
            </div>
          ) : (
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
          >
              {millionViewInventories.map((inventory, index) => (
              <div
                key={inventory.id}
                className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-2 sm:px-3"
              >
                <Card className="card-hover h-full overflow-hidden">
                  <div className="relative aspect-video w-full">
                    <img
                      src={inventory.imageUrl}
                      alt={inventory.title}
                      className="w-full h-full object-cover object-center"
                    />
                    <Badge
                      className={`absolute top-2 sm:top-3 right-2 sm:right-3 text-xs font-medium px-2 py-1 ${getCategoryColor(inventory.category)}`}
                      data-testid={`badge-category-${inventory.id}`}
                    >
                      {inventory.category}
                    </Badge>
                    <div className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-white/95 backdrop-blur-sm rounded-lg px-2 sm:px-3 py-1">
                      <div className="flex items-center gap-1 text-xs sm:text-sm font-semibold text-gray-900">
                        <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-primary-600 flex-shrink-0" />
                        <span className="whitespace-nowrap">{inventory.viewsPerWeek?.toLocaleString() ?? 'N/A'}</span>
                        <span className="text-xs text-gray-600 hidden sm:inline">Impression</span>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-3 sm:p-4 lg:p-6">
                    <h3
                      className="text-base sm:text-lg font-semibold text-gray-900 mb-2 line-clamp-1"
                      title={inventory.title?.length > 30 ? inventory.title : ""}
                    >
                      {inventory.title?.length > 30
                        ? `${inventory.title.slice(0, 30)}...`
                        : inventory.title}
                    </h3>
                    <div className="flex items-center text-gray-600 mb-3 sm:mb-4">
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                      <span className="text-xs sm:text-sm truncate">{inventory.location}</span>
                    </div>
                    <div className="flex justify-between items-center mb-3 sm:mb-4">
                      <div className="flex items-center text-forest-custom font-bold text-sm sm:text-base">
                        <span className="whitespace-nowrap">
                          {typeof inventory.price === 'number' && !isNaN(inventory.price)
                              ? `${inventory.currencyCode || 'N/A'} ${inventory.price?.toLocaleString()}`
                              : `${inventory.currencyCode || 'N/A'} N/A`}/Day
                        </span>
                        {/* <span className="text-xs font-normal text-gray-600 ml-1">/week</span> */}
                      </div>
                    </div>
                    <Button
                      className="w-full btn-primary-gradient hover:bg-primary-700 text-white font-medium py-2.5 transform hover:scale-[1.02] transition-all duration-200"
                      data-testid={`button-view-million-${inventory.id}`}
                      onClick={() => {
                        const billboardType = inventory?.category || inventory.type || inventory.billboardType;
                        if (!isLoggedIn) {
                          if (!isExploreEnabled && isLoginEnabled) {
                            // Hide button before login if isExploreEnabled is false
                            return null;
                          }
                          if (!isLoginEnabled) {
                            // Show button before login, navigate to billboard/
                            navigate(`${base_path}/billboard/${inventory.id}?type=${encodeURIComponent(billboardType)}`, {
                              state: {
                                  currentPrice: inventory.price,
                                  priceFormat: 'landing-page-million'
                              }
                            });
                            return;
                          }
                          // Default: navigate to billboard/
                          navigate(`${base_path}/billboard/${inventory.id}?type=${encodeURIComponent(billboardType)}`, {
                            state: {
                                currentPrice: inventory.price,
                                priceFormat: 'landing-page-million'
                            }
                          });
                        } else {
                          if (!isExploreEnabled) {
                            // Show button after login, navigate to property-details
                            navigate(`${base_path}/property-details/${inventory.id}?type=${encodeURIComponent(billboardType)}`, {
                              state: {
                                currentPrice: inventory.price,
                                startDate: new Date().toISOString(),
                                endDate: (() => {
                                  const now = new Date();
                                  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                                  return lastDay.toISOString();
                                  })(),
                                  priceFormat: 'landing-page-million'
                              }
                            });
                            return;
                          }
                          if (!isLoginEnabled) {
                            // Hide button after login
                            return null;
                          }
                          // Default: navigate to property-details
                          navigate(`${base_path}/property-details/${inventory.id}?type=${encodeURIComponent(billboardType)}`, {
                            state: {
                              currentPrice: inventory.price,
                              startDate: new Date().toISOString(),
                              endDate: (() => {
                                const now = new Date();
                                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                                return lastDay.toISOString();
                                })(),
                                priceFormat: 'landing-page-million'
                            }
                          });
                        }
                      }}
                      style={
                        (!isLoggedIn && !isExploreEnabled && isLoginEnabled) || (isLoggedIn && !isLoginEnabled) ? { display: 'none' } : {}
                      }
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Progress indicators */}
        <div className="flex justify-center mt-6 sm:mt-8 space-x-2">
          {Array.from({ length: Math.max(millionViewInventories.length - itemsPerView + 1, 1) }).map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-colors ${index === currentIndex ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              onClick={() => setCurrentIndex(index)}
              data-testid={`indicator-${index}`}
            />
          ))}
        </div>

        {/* CTA Section */}
        {((isLoggedIn && isExploreEnabled) || (!isLoggedIn && isLoginEnabled && isExploreEnabled) || (!isLoggedIn && !isLoginEnabled)) && (
          <div className="text-center mt-8 sm:mt-12 px-4 sm:px-0">
            <Button
              onClick={() => {
                localStorage.setItem('clearStartCampaignFilters', 'true');
                if (isLoggedIn) {
                  navigate(`${base_path}/propertylist`);
                } else {
                  navigate(`${base_path}/explore`);
                }
              }}
              size="lg"
              className="bg-forest-custom hover:bg-forest-hover text-white px-6 sm:px-8 py-2.5 sm:py-3 w-full sm:w-auto text-sm sm:text-base"
              data-testid="button-view-all-million"
            >
              View All Million+ View Locations
            </Button>
          </div>
        )}
      </div>

    </section>
  );
}